import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { maskVpa } from '../../utils/maskVpa';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LockIcon from '@mui/icons-material/Lock';
// [CASH-MODE] UPI / Cash filter + indication
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, totalsByModeFromRows } from '../../utils/paymentMode';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';

const PoolTransactions = () => {
  // State Variables
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessages, setSnackbarMessages] = useState([]);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [page, setPage] = useState(0); // Zero-based page index
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [totalRows, setTotalRows] = useState(0);
  const [userId, setUserId] = useState(null); // User ID from JWT
  const [userLevel, setUserLevel] = useState(null); // User level from JWT
  const { can } = usePermissions();
  const canAdjustPool = can('reports.pool.adjust');
  const [currentBalance, setCurrentBalance] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Locked = rewards on active game cards (released on play, returned to pool on expiry)
  const [lockedAmount, setLockedAmount] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [lockedOpen, setLockedOpen] = useState(false);
  const [lockedCards, setLockedCards] = useState([]);
  const [lockedLoading, setLockedLoading] = useState(false);

  // Add / Deduct Pool Cashback State
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [formActionType, setFormActionType] = useState('add'); // 'add' | 'withdraw'
  const [formAmount, setFormAmount] = useState('');
  const [formVpaId, setFormVpaId] = useState('ADMIN_POOL_ACCOUNT');
  const [formComment, setFormComment] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Filter States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [actionType, setActionType] = useState('all');
  const [head, setHead] = useState('all'); // all | service_charge (fee kept from withdrawals)
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE] only 'add' rows carry a mode
  const [apiTotalsByMode, setApiTotalsByMode] = useState(null); // [CASH-MODE] API totalsByMode when provided
  
  // Summary States
  const [summary, setSummary] = useState({
    totalAdd: 0,
    totalWithdraw: 0,
    countAdd: 0,
    countWithdraw: 0
  });

  const navigate = useNavigate();

  // Initialize dates (end date = today, start date = 10 days ago)
  useEffect(() => {
    const today = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(today.getDate() - 10);

    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(tenDaysAgo.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    // Extract userId from JWT token stored in localStorage
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
        setUserLevel(decodedToken.level);
      } catch (error) {
        console.error('Error decoding token:', error);
        setSnackbarMessages(['Error decoding user token']);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        navigate('/login');
      }
    } else {
      console.error('No token found in localStorage');
      setSnackbarMessages(['No user token found']);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      navigate('/login');
    }
  }, [navigate]);

  // Fetch current pool balance
  const fetchCurrentBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const token = sessionStorage.getItem('token');

      if (!token) {
        setSnackbarMessages(['No authentication token found. Please login again.']);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/pool-transactions/balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCurrentBalance(response.data.balance || 0);
      setLockedAmount(response.data.lockedAmount || 0);
      setTotalBalance(response.data.totalBalance ?? (response.data.balance || 0));
      setLastUpdated(response.data.lastUpdated);
    } catch (error) {
      console.error('Error fetching pool balance:', error);

      if (error.response && error.response.status === 401) {
        setSnackbarMessages(['Authentication failed. Please login again.']);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        // Clear invalid token
        sessionStorage.removeItem('token');
        navigate('/login');
        return;
      }

      setSnackbarMessages(['Error fetching pool balance. Please try again.']);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setBalanceLoading(false);
    }
  }, [navigate]);

  // Fetch transactions with filters
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        setSnackbarMessages(['No authentication token found. Please login again.']);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        navigate('/login');
        return;
      }

      const params = {
        page: page + 1, // API expects page to start from 1
        limit: pageSize,
        startDate,
        endDate,
        actionType: actionType === 'all' ? '' : actionType,
        head: head === 'all' ? undefined : head,
        paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
      };

      const response = await axios.get(`${API_BASE_URL}/pool-transactions`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTransactions(response.data.transactions || []);
      setTotalRows(response.data.totalTransactions || 0);
      setApiTotalsByMode(response.data.totalsByMode || response.data.summary?.totalsByMode || null); // [CASH-MODE]
      setSummary(response.data.summary || {
        totalAdd: 0,
        totalWithdraw: 0,
        countAdd: 0,
        countWithdraw: 0
      });
    } catch (error) {
      console.error('Error fetching transactions:', error);

      if (error.response && error.response.status === 401) {
        setSnackbarMessages(['Authentication failed. Please login again.']);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        // Clear invalid token
        sessionStorage.removeItem('token');
        navigate('/login');
        return;
      }

      if (error.response && error.response.data && error.response.data.message) {
        setSnackbarMessages([error.response.data.message]);
      } else {
        setSnackbarMessages(['Error fetching transactions']);
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, startDate, endDate, actionType, head, paymentMode, navigate]); // [CASH-MODE] paymentMode dep

  useEffect(() => {
    if (userId) {
      fetchCurrentBalance();
    }
  }, [userId, fetchCurrentBalance]);

  useEffect(() => {
    if (userId && startDate && endDate) {
      fetchTransactions();
    }
  }, [userId, startDate, endDate, fetchTransactions]);

  // Handle filter application
  const handleApplyFilters = () => {
    setPage(0); // Reset to first page
    fetchTransactions();
  };

  // Handle refresh
  // Locked rewards list (active game cards holding pool money)
  const openLockedRewards = async () => {
    setLockedOpen(true);
    setLockedLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/pool-transactions/locked`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLockedCards(response.data.cards || []);
    } catch (error) {
      console.error('Error fetching locked rewards:', error);
      setLockedCards([]);
    } finally {
      setLockedLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchCurrentBalance();
    fetchTransactions();
  };

  // Open Add/Deduct Modal
  const handleOpenAddModal = (action = 'add') => {
    if (!canAdjustPool) return;
    setFormActionType(action);
    setFormAmount('');
    setFormVpaId('ADMIN_POOL_ACCOUNT');
    setFormComment('');
    setAddModalOpen(true);
  };

  // Execute Add / Deduct Pool Transaction API
  const handleSavePoolTransaction = async () => {
    if (!canAdjustPool) return;
    const numAmount = parseFloat(formAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setSnackbarMessages(['Please enter a valid amount greater than 0']);
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (formActionType === 'withdraw' && numAmount > currentBalance) {
      setSnackbarMessages([`Insufficient pool balance (${formatCurrency(currentBalance)}) for deduction of ${formatCurrency(numAmount)}`]);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setFormLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/pool-transactions`, {
        vpaId: formVpaId.trim() || 'ADMIN_POOL_ACCOUNT',
        poolAmount: numAmount,
        actionType: formActionType,
        comment: formComment.trim() || `Manual Pool Cashback ${formActionType === 'add' ? 'Top-up' : 'Deduction'}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSnackbarMessages([`Pool cashback ${formActionType === 'add' ? 'added' : 'deducted'} successfully!`]);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setAddModalOpen(false);
        handleRefresh();
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to process pool transaction';
      setSnackbarMessages([msg]);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setFormLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', options);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  // DataGrid Columns
  const columns = [
    {
      field: 'transactionNumber',
      headerName: 'S.No',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        if (params.row.transactionNumber) {
          return params.row.transactionNumber;
        }
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (page * pageSize) + (rowIndex !== undefined && rowIndex !== null ? rowIndex + 1 : 1);
      }
    },
    { 
      field: 'vpaId', 
      headerName: 'VPA ID', 
      width: 200,
      renderCell: (params) => maskVpa(params.row.vpaId, userLevel)
    },
    {
      field: 'poolAmount',
      headerName: 'Amount',
      width: 150,
      renderCell: (params) => (
        <Typography
          sx={{
            color: params.row.actionType === 'add' ? 'green' : 'red',
            fontWeight: 'bold',
          }}
        >
          {formatCurrency(params.row.poolAmount)}
        </Typography>
      ),
    },
    {
      field: 'actionType',
      headerName: 'Action Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.actionType === 'withdraw' ? 'CLAIMED' : params.row.actionType.toUpperCase()}
          color={params.row.actionType === 'add' ? 'success' : 'error'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'head',
      headerName: 'Head',
      width: 140,
      sortable: false,
      renderCell: (params) => (params.row.head
        ? <Chip label={params.row.headLabel || 'Service Charge'} size="small" sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
        : '—'),
    },
    { field: 'comment', headerName: 'Comment', width: 250 },
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDate(params.row.createdAt)}
        </Typography>
      ),
    },
    // [CASH-MODE] UPI / Cash badge (withdraw rows have no mode)
    {
      field: 'paymentMode',
      headerName: 'Mode',
      width: 100,
      sortable: false,
      renderCell: (params) => (params.row.actionType === 'add' && !params.row.head ? <PaymentModeChip row={params.row} /> : '—'),
    },
  ];

  // [CASH-MODE] added-cashback split; API summary.totalsByMode may key the sum as totalAdd or amount
  const poolModeTotals = apiTotalsByMode
    || totalsByModeFromRows(transactions.filter((t) => t.actionType === 'add'), (t) => t.poolAmount);
  const poolModeAmountKey = poolModeTotals?.UPI?.totalAdd !== undefined ? 'totalAdd' : 'amount';

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      {/* Place BackButton outside the Grid */}
      <BackButton />
      <br /> <br />
      <Box sx={{ width: '100%', p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Cashback Account and Transactions
        </Typography>

        {/* Balance Card */}
        <Card sx={{ mb: 3, background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
          <CardContent>
            <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  Available Pool Balance
                </Typography>
                {balanceLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  <>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {formatCurrency(currentBalance)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 0.5 }}>
                      <Chip
                        icon={<LockIcon sx={{ color: '#7c2d12 !important' }} />}
                        label={`Locked: ${formatCurrency(lockedAmount)}`}
                        onClick={openLockedRewards}
                        sx={{ bgcolor: '#fde68a', color: '#7c2d12', fontWeight: 'bold', cursor: 'pointer' }}
                      />
                      <Chip
                        label={`Total (Available + Locked): ${formatCurrency(totalBalance)}`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 'bold' }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block' }}>
                      Locked = rewards on active game cards. Played: shown as withdraw. Expired: returned to pool.
                    </Typography>
                  </>
                )}
                {lastUpdated && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Last updated: {formatDate(lastUpdated)}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1.5, flexWrap: 'wrap' }}>
                {canAdjustPool && (
                <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAddModal('add')}
                  sx={{ fontWeight: 'bold', px: 2, py: 1 }}
                >
                  Add Pool Cashback
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RemoveIcon />}
                  onClick={() => handleOpenAddModal('withdraw')}
                  sx={{ fontWeight: 'bold', px: 2, py: 1 }}
                >
                  Deduct Pool Cashback
                </Button>
                </>
                )}
                <Button
                  variant="contained"
                  onClick={handleRefresh}
                  startIcon={<RefreshIcon />}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  Total Added
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(summary.totalAdd)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.countAdd} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  Total Withdrawn
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(summary.totalWithdraw)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.countWithdraw} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary.main">
                  Net Flow
                </Typography>
                <Typography 
                  variant="h5" 
                  fontWeight="bold"
                  color={(summary.totalAdd - summary.totalWithdraw) >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(summary.totalAdd - summary.totalWithdraw)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  For selected period
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#92400e' }}>
                  Service Charge
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(summary.totalServiceCharge || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.countServiceCharge || 0} withdrawals (part of Total Added)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  Total Transactions
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {summary.countAdd + summary.countWithdraw}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  For selected period
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* [CASH-MODE] */}
        <PaymentModeTotals totals={poolModeTotals} amountKey={poolModeAmountKey} title="Added" sx={{ mb: 3 }} />

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom startIcon={<FilterListIcon />}>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  label="Action Type"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="add">Add</MenuItem>
                  <MenuItem value="withdraw">Claimed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Head</InputLabel>
                <Select
                  value={head}
                  onChange={(e) => { setHead(e.target.value); setPage(0); }}
                  label="Head"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="service_charge">Service Charge</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* [CASH-MODE] */}
            <Grid item xs={12} sm={6} md={3}>
              <PaymentModeFilter value={paymentMode} onChange={(m) => { setPaymentMode(m); setPage(0); }} size="medium" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                fullWidth
                startIcon={<FilterListIcon />}
                disabled={loading}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* DataGrid */}
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={transactions}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[5, 10, 20, 50]}
            pagination
            page={page}
            rowCount={totalRows}
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0); // Reset to first page when page size changes
            }}
            getRowId={(row) => row._id}
            disableSelectionOnClick
            loading={loading}
            components={{
              NoRowsOverlay: () => (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Typography>No transactions found for the selected criteria</Typography>
                </Box>
              ),
              LoadingOverlay: () => (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <CircularProgress />
                </Box>
              ),
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)', // Light blue on hover
              },
            }}
          />
        </Box>

        {/* Locked Rewards Dialog */}
        <Dialog open={lockedOpen} onClose={() => setLockedOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon sx={{ color: '#b45309' }} /> Locked Rewards ({lockedCards.length}) - {formatCurrency(lockedAmount)}
          </DialogTitle>
          <DialogContent dividers>
            {lockedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : lockedCards.length === 0 ? (
              <Typography color="text.secondary">No rewards are locked right now.</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: 8 }}>#</th>
                      <th style={{ padding: 8 }}>VPA ID</th>
                      <th style={{ padding: 8 }}>Mode</th>
                      <th style={{ padding: 8 }}>Paid (₹)</th>
                      <th style={{ padding: 8 }}>Reward %</th>
                      <th style={{ padding: 8 }}>Locked (₹)</th>
                      <th style={{ padding: 8 }}>Card Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockedCards.map((c, i) => (
                      <tr key={c._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: 8 }}>{i + 1}</td>
                        <td style={{ padding: 8 }}>{maskVpa(c.vpaId, userLevel)}</td>
                        <td style={{ padding: 8 }}>{c.paymentMode}</td>
                        <td style={{ padding: 8 }}>{Number(c.totalAmount || 0).toFixed(2)}</td>
                        <td style={{ padding: 8 }}>{Number(c.offerPercentage || 0)}%</td>
                        <td style={{ padding: 8, fontWeight: 'bold', color: '#b45309' }}>{Number(c.offerAmount || 0).toFixed(4)}</td>
                        <td style={{ padding: 8 }}>{c.createdAt ? formatDate(c.createdAt) : c.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLockedOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Add / Deduct Pool Cashback Dialog */}
        <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ bgcolor: formActionType === 'add' ? 'success.main' : 'error.main', color: 'white', fontWeight: 'bold' }}>
            {formActionType === 'add' ? 'Add Pool Cashback (+)' : 'Deduct Pool Cashback (-)'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={formActionType}
                  onChange={(e) => setFormActionType(e.target.value)}
                  label="Action Type"
                >
                  <MenuItem value="add">Add / Credit (+)</MenuItem>
                  <MenuItem value="withdraw">Deduct / Debit (-)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="VPA / Account ID"
                placeholder="e.g. ADMIN_POOL_ACCOUNT"
                value={formVpaId}
                onChange={(e) => setFormVpaId(e.target.value)}
              />

              <TextField
                fullWidth
                type="number"
                label="Amount (₹)"
                placeholder="e.g. 1000"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
              />

              <TextField
                fullWidth
                label="Comment / Remarks"
                placeholder="e.g. Manual cashback pool top-up"
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setAddModalOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSavePoolTransaction}
              variant="contained"
              color={formActionType === 'add' ? 'success' : 'error'}
              disabled={formLoading}
            >
              {formLoading ? <CircularProgress size={24} color="inherit" /> : `Confirm ${formActionType === 'add' ? 'Add' : 'Deduct'}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {Array.isArray(snackbarMessages)
              ? snackbarMessages.join('\n')
              : snackbarMessages}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default PoolTransactions;